import type { UserRole } from './userRole.ts';

export type RoleGuideId = UserRole | 'dancer';

export interface RoleGuideEntry {
  id: RoleGuideId;
  label: string;
  summary: string;
  can: readonly string[];
  cannot: readonly string[];
}

/**
 * One reviewable source for the plain-language role guide. It is deliberately
 * separate from Firestore authorization: the rules remain the security
 * boundary, while this list keeps Kim's explanation copy from drifting across
 * Manage Users and future parent-facing help cards.
 */
export const ROLE_GUIDE: readonly RoleGuideEntry[] = [
  {
    id: 'director',
    label: 'Director',
    summary: 'Full studio authority and the final approval point.',
    can: [
      'See and manage all classes, dancers, staff, attendance, messages, and studio records.',
      'Manage user accounts and roles.',
      'Approve costumes and room requests, manage the Recital/Comp Tracker, and clear injuries.',
      'Publish studio announcements and manage their history.',
    ],
    cannot: [
      'A Director account does not become a parent or dancer account unless it is linked to that family or dancer.',
    ],
  },
  {
    id: 'manager',
    label: 'Manager',
    summary: 'Everything front desk and an Executive Teacher can do, without the Director-only final approvals.',
    can: [
      'See every class, roster, teacher assignment, and student attendance record.',
      'Mark all Jr Staff attendance and manage every student project tracker.',
      'Write and update injury records (notes and the doctor-note flag), staff notes, and dancer to-dos.',
      'Use normal teacher tools for classes they personally teach.',
      'Edit any class in the studio: rename it and change its type, style, level, ages, capacity, color, photo, song, teaching team, Jr assistants, the family facing note and the recital slot.',
      'Add, change and remove a class\'s weekly times, with the booked sessions moved or taken off to match, and use Cancel a class session for one day.',
      'Do everything front desk does: open hours, seasons, countdowns, registrations, workshop signups, Bag Groups, costume order tracking, and rosters.',
      'Send a studio-wide announcement to every active family and staff account.',
    ],
    cannot: [
      'Manage user accounts or change roles.',
      'Approve costume designs or control music and costume decisions.',
      'Approve room requests, or delete a booking somebody else created.',
      'Clear an injury (mark a dancer cleared to dance) or delete an injury record. Both are Director only.',
      'Edit or remove a studio announcement after it is sent.',
      'Place a registration into a class.',
      'Create or delete a class. Both stay with the Director.',
      'Set alternates, edit the comp day look on a class they do not teach, or take music and videos off a class they do not teach. Adding media is theirs on every class.',
      'Write the internal only note on a class they do not teach. They can read it.',
    ],
  },
  {
    id: 'company',
    label: 'Executive Teacher',
    summary: 'Adult teacher access for classes, dancers, and staff coordination.',
    can: [
      'Teach assigned classes, use class boards, and record dancer and Jr Staff attendance.',
      'See dancer rosters, parent contacts, staff notes, injuries, and student project trackers.',
      'Message families and staff and submit Student Awards or shout-outs.',
      'See every colleague\'s email address on the Staff page. Phone numbers stay private.',
    ],
    cannot: [
      'Manage user roles or studio settings.',
      'Approve costumes or room requests.',
      'Use Director-only Recital/Comp Tracker decisions.',
    ],
  },
  {
    id: 'ensemble',
    label: 'Jr Staff',
    summary: 'A focused teaching-assistant view that protects private adult records.',
    can: [
      'See and help with assigned classes, class boards, attendance, to-dos, and the sub board.',
      'See the studio attendance report and the attendance concerns list.',
      'See the injury log and log an injury. Only a Director clears one.',
      'Message staff and submit Student Awards or shout-outs.',
      'See every colleague\'s email address on the Staff page. Phone numbers stay private.',
    ],
    cannot: [
      'Change or clear an injury record after it is logged.',
      'See private staff notes or parent contact details.',
      'Mark Jr Staff attendance, their own or another assistant\'s.',
      'Manage users, rooms, costumes, or studio-wide announcements.',
    ],
  },
  {
    id: 'foh',
    label: 'Admin',
    summary: 'Front-desk and studio-operations access without Director authority.',
    can: [
      'See classes, dancers, parent contacts, schedules, and assigned studio operations.',
      'See and update injury records (clearing one is Director only), and see Jr Staff attendance records.',
      'See the studio attendance report and the attendance concerns list.',
      'Coordinate staff, families, bags, signups, and costume pickup status.',
      'See every colleague\'s email address on the Staff page. Phone numbers stay private.',
    ],
    cannot: [
      'Manage user roles or Director settings.',
      'Mark Jr Staff attendance. Executive Teachers, Managers and Directors do that.',
      'Approve costumes or make Director-only Recital/Comp Tracker decisions.',
      'Clear records reserved for the Director when the screen says Director only.',
    ],
  },
  {
    id: 'family',
    label: 'Parent or grown-up',
    summary: 'One clean family view for every dancer linked to the account.',
    can: [
      'See linked dancers, their classes, calendars, approved costumes and media, tasks, and announcements.',
      'Submit absences and manage a linked dancer login.',
      'See every message connected to each linked dancer, including messages that dancer posts on a class board.',
    ],
    cannot: [
      'See another family\'s private information.',
      'See staff-only notes, injury records, internal attendance records, or adult studio tools.',
      'Start or receive a private dancer-studio conversation that is hidden from linked grown-ups.',
    ],
  },
  {
    id: 'dancer',
    label: 'Dancer',
    summary: 'A child-safe view of the dancer\'s own studio life.',
    can: [
      'See their own classes, class boards, to-dos, family messages, policies, and help.',
      'Complete their own to-dos, acknowledge family messages, and post to their own class boards.',
    ],
    cannot: [
      'Privately message the studio without linked grown-ups being able to see it.',
      'See other dancers\' records, attendance, injuries, staff notes, parent contacts, or adult tools.',
    ],
  },
];

export function isManagerRole(role: UserRole | string | null | undefined): boolean {
  return role === 'manager';
}

/**
 * THE MANAGER UNION (Bobbie, 2026-08-13): "Managers should be able to do
 * everything that admin and executive teachers can do." Every screen that used
 * to read `role === 'foh'` for an Admin power is now this predicate instead.
 *
 * These two are the CLIENT MIRRORS of isFohOrManagerByDoc() and
 * isDirectorBootstrapOrFohOrManager() in shared/firestore.rules. They live
 * here, in one file, because docs/lessons.md has this failure twice: a
 * permission fixed on one page and not on the page beside it, and a control
 * the rules would allow that no screen ever draws. A gate spelled out inline
 * on a page cannot be kept in step with the rule it mirrors.
 */
export function isFohOrManager(role: UserRole | null | undefined): boolean {
  return role === 'foh' || role === 'manager';
}

export function isDirectorFohOrManager(role: UserRole | null | undefined): boolean {
  return role === 'director' || isFohOrManager(role);
}

/**
 * WHO MANAGES BOARD MEMBERSHIP (task #31, Bobbie 2026-08-30, verbatim,
 * asked directly "Who can add and remove people on the board, besides
 * you?": "Directors + managers"). Client mirror of
 * shared/firestore.rules' isActiveUserInRoles(['director', 'manager']) on
 * every *BoardMembershipOverlay/current write. Deliberately NARROWER than
 * isDirectorFohOrManager above - foh is not part of this ruling, and this
 * predicate must never silently widen if that one ever does.
 */
export function isDirectorOrManager(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager';
}

/**
 * WHO OPENS THE SUBBING REPORT (/payroll). Bobbie, 2026-09-24 about 1:05pm
 * Central, verbatim: "Yes managers can." Her 2026-09-16 answer had already
 * put the report "under reports under the nav menu for directors and
 * managers only", and 2026-09-24 about 12:10am she ruled Jr Staff out
 * ("cannot read the subbing report", DECISIONS 44). So: an ACTIVE Director or
 * Manager, and nobody else. Jr Staff are ensemble on either sign-in, so both
 * of hers answer false here, and so do Admin, Executive Teachers, families
 * and dancers.
 *
 * Client mirror of the payrollReviews rule in shared/firestore.rules
 * (isActiveUserInRoles(['director', 'manager'])). The page gate, the nav
 * drawer rows, the Command Center rows and the How-To guide entry all hang
 * off this one answer so they cannot drift apart.
 *
 * NOT the same question as who may Record a cover or Remove this record on
 * that page: those stay Director only (the subRecordCover lane and the
 * subRequests cancel rule), so the page keeps its own Director check for
 * those two controls.
 */
export function canOpenSubbingReport(
  role: UserRole | null | undefined,
  status: string | null | undefined
): boolean {
  return status === 'active' && isDirectorOrManager(role);
}

/**
 * WHO OPENS UPLOAD SPEED (/upload-speed). Speed plan 2026-10-19, packet P1:
 * "One upload record, readable by a Director". Bobbie, 2026-10-19: "the
 * speed thing is maybe the largest issue we have." An ACTIVE Director and
 * nobody else, as the plan names. The records carry no names, so this is
 * not a privacy line; widening it is Bobbie's call, and this line and the
 * server's own check in netlify/functions/uploadRecords.mjs change together.
 * The page gate, the nav drawer row and the How-To entry all hang off it.
 */
export function canOpenUploadSpeedReport(
  role: UserRole | null | undefined,
  status: string | null | undefined
): boolean {
  return status === 'active' && role === 'director';
}

/**
 * WHO OPENS STAFF ACTIVITY (/staff-activity). Bobbie, 2026-09-28 about 9am
 * Central, asked whether staff are using the Hub beyond attendance ("are
 * they uploading videos and music?"), then "great build the staff
 * activitie". Packet SA built it for an ACTIVE Director and nobody else,
 * because only Bobbie decides who else sees a report about her staff. If she
 * later opens it to Managers, this line and the server's own check in
 * netlify/functions/staffActivityReport.mjs change together, nothing else.
 *
 * The page gate, the nav drawer row, the Command Center rows and the How-To
 * entry all hang off this one answer so they cannot drift apart. A Jr Staff
 * is 'ensemble' on either sign-in, so both of hers answer false here.
 */
export function canOpenStaffActivity(
  role: UserRole | null | undefined,
  status: string | null | undefined
): boolean {
  return status === 'active' && role === 'director';
}

/**
 * WHO FINDS AND PUTS BACK A HIDDEN BOARD POST (/removed-posts). An ACTIVE
 * Director and nobody else. Built 2026-10-01 after a staff post to the comp
 * parents vanished and Bobbie could not reach the Firebase console from her
 * phone ("I can't do that from my phone I don't think"). Any staff account
 * could hide a post on the Comp Parent Board then (only a Director or the
 * author can since that evening, canRemoveOthersBoardPosts below), so who
 * may put one back is deliberately the narrowest door there is; widening it
 * is Bobbie's call, and
 * this line and the server's own check in
 * netlify/functions/boardModeration.mjs change together, nothing else.
 *
 * The page gate, the Removed posts link on all three boards and the How-To
 * entry hang off this one answer so they cannot drift apart. A Jr Staff is
 * 'ensemble' on either sign-in, so both of hers answer false here.
 */
export function canRestoreBoardPosts(
  role: UserRole | null | undefined,
  status: string | null | undefined
): boolean {
  return status === 'active' && role === 'director';
}

/**
 * WHO SEES REMOVE ON SOMEBODY ELSE'S BOARD POST OR REPLY. Bobbie, 2026-10-01,
 * verbatim, about 2:58pm Central: "Only directors can remove other people's
 * posts". An ACTIVE Director and nobody else; an author still gets Take this
 * back on her own words, which is a different question (whose words are
 * these) and not this one.
 *
 * NOT THE PERMISSION. The screen follows the server, never the other way
 * round: netlify/functions/boardModeration.mjs action 'remove' (the Comp
 * Parent Board, the Jr Staff board and the Comp Team board's Parent tips,
 * since #1309), netlify/functions/lib/compParentsBoard.mjs (the New Comp
 * Parent Board) and shared/firestore.rules' isCompBoardAuthorOrDirector (the
 * Comp Team board's own posts, retracted, and their replies, deleted) each
 * ask the same question for themselves. A Parent tip is NOT a rules question
 * any more: the compTips delete rule is closed to every client. This only
 * stops a button being drawn that would be refused.
 * Both of a Jr Staff's sign-ins are 'ensemble', so both answer false.
 */
export function canRemoveOthersBoardPosts(
  role: UserRole | null | undefined,
  status: string | null | undefined
): boolean {
  return status === 'active' && role === 'director';
}

/**
 * WHO OPENS THE COSTUME PICKUP REPORT (/costume-pickups). Bobbie, 2026-09-25,
 * verbatim: "we also need a report in the command center for all staff that
 * can see what has been picked up and what remains". So every ACTIVE staff
 * role: Director, Manager, Executive Teacher (company), Admin (foh) and Jr
 * Staff (ensemble). Families and dancers never.
 *
 * JR STAFF ARE IN BECAUSE SHE SAID "ALL STAFF", AND ONE LINE TAKES THEM OUT.
 * Her 2026-09-01 ruling keeps Jr Staff out of "the outside reporting", and
 * this report is the newer, more specific word, so they are in by default;
 * that tension is flagged to her rather than decided here. A Jr Staff is ONE
 * PERSON however she signs in: both of her sign-ins carry role 'ensemble' on
 * users/{uid}, so this answers the same for either. The rules already let her
 * read everything the report needs (offerings, costumes and students are all
 * isActiveStaffByDoc() reads, which lists 'ensemble'), so nothing about the
 * rules changes either way. To take Jr Staff out, delete 'ensemble' below;
 * the page, the nav rows, the Command Center tile and the guide entry all
 * follow this one answer.
 *
 * WHO SEES A NAME against a pickup is a different, narrower question: see
 * canSeeCostumePickupNames.
 */
const COSTUME_PICKUP_REPORT_ROLES: readonly UserRole[] = ['director', 'manager', 'company', 'foh', 'ensemble'];

export function canOpenCostumePickupReport(
  role: UserRole | null | undefined,
  status: string | null | undefined
): boolean {
  return status === 'active' && !!role && COSTUME_PICKUP_REPORT_ROLES.includes(role);
}

/**
 * Who reads WHICH GROWN-UP marked a bag, by name, on the pickup report: the
 * Director, Managers and Admin, the three roles that already see family names
 * on the costume status board (CostumeStatus.tsx, isDirectorFohOrManager) and
 * that can read other accounts at all. Everyone else on the report sees
 * "Front desk" or "Parent", which is also all the page asks Firestore for.
 */
export function canSeeCostumePickupNames(role: UserRole | null | undefined): boolean {
  return isDirectorFohOrManager(role);
}

/**
 * KIM'S COSTUME REPORT (/costume-report, Bobbie 2026-11-02). The roles that
 * track costume orders (R15) and run the pickup desk: an active Director,
 * Manager or Admin. The same three netlify/functions/costumeRecords.mjs
 * admits (COSTUME_PICKUP_DESK_ROLES), so the page and the server agree.
 */
export function canOpenCostumeReport(
  role: UserRole | null | undefined,
  status: string | null | undefined
): boolean {
  return status === 'active' && isDirectorFohOrManager(role);
}

/**
 * WHO CAN PUT A PHOTO ON A COSTUME CARD (Bobbie, 2026-09-20, verbatim): "All
 * teachers managers, Director should be able to upload costumes photos, etc.
 * Director should be able to upload it to any class. Teacher should only be
 * able to upload it to their own classes. Manager should be able to upload it
 * to any class." Asked one at a time on 2026-09-21 about the two cases her
 * words did not settle: a Jr Staff, "No, not Jr Staff"; a Manager creating the
 * card rather than only photographing one, "Yes, create cards too".
 *
 * ROLE ONLY. The class half of her ruling, that an Executive Teacher reaches
 * HER OWN classes, is not a role question and is not decided here: the rules
 * recheck the offering on every write (costumeStaff() in
 * shared/firestore.rules, canWriteCostumeImage() in shared/storage.rules).
 * This predicate answers "may this role ever touch a costume photo", which is
 * what a guide entry and a screen need.
 *
 * JR STAFF ARE IN, AND IT TOOK THREE QUESTIONS TO GET THERE. Bobbie's
 * 2026-09-21 answer was "No, not Jr Staff", but that question was framed as
 * GRANTING a power and never said a "no" would REMOVE one: an assigned Jr
 * Staff could already do this, because both rule lanes ran on
 * isActiveStaffByDoc(), which includes 'ensemble'. Asked again that evening
 * with the removal stated plainly, she chose "Yes, for her own class only",
 * keeping her 2026-09-01 ruling: "they need to be able to do everything a
 * teacher can in the class they teach but none of the outside reporting".
 *
 * NOT isGrowthEvaluatorRole, which no longer even holds the same roles. That
 * one is about children's progress reports and Jr Staff are deliberately OUT
 * of it, which is the "none of the outside reporting" half of the same ruling;
 * a costume photo must not follow it. Same reason isDirectorOrManager above
 * refuses to lean on isDirectorFohOrManager.
 *
 * A SCREEN NEEDS MORE THAN THIS. Role alone cannot answer "her own class":
 * pair it with isRulesOfferingTeacher() from lib/offeringTeachers, which is the
 * client mirror of costumeStaff(). This predicate is for a guide entry and for
 * "may this role ever".
 */
export function canUploadCostumePhoto(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager' || role === 'company' || role === 'ensemble';
}

/**
 * WHO CAN BE PUT IN A BAG GROUP (Bobbie, 2026-08-25). The bag-group leads
 * asked for named staff to be added to specific bags: "the admin leads the
 * charge with the bags so ... i would like to get the following people added
 * into specific groups". Bobbie ruled Admin and Managers in, teaching staff
 * out.
 *
 * Until this ruling both pickers listed `role === 'family'` and nothing else,
 * so an Admin account simply did not appear and there was no way to find out
 * why. That is the bug this fixes.
 *
 * Director is in the list for the picker's sake only. A Director already
 * reads and posts in every bag group WITHOUT being a member, so adding one
 * changes nothing about her access; it just stops the picker hiding her.
 *
 * Client mirror of isAuthorizedBagMessageCaller() in shared/firestore.rules,
 * which is where the boundary is actually enforced. MEMBERSHIP REMAINS THE
 * GATE for everyone but the Director: passing this predicate only makes an
 * account eligible to be added, it grants nothing on its own.
 */
export function canJoinBagGroup(role: UserRole | null | undefined): boolean {
  return role === 'family' || isDirectorFohOrManager(role);
}

/** The author label stamped on a bag-group chat message. */
export type BagGroupAuthorRole = 'family' | 'director' | 'staff';

/**
 * The value stamped on a bag-group chat message as its author role. The rule
 * checks this against the live profile document in both directions, so a
 * parent cannot post as staff and an Admin cannot post as a parent or as the
 * Director. Anything that is not staff or Director posts as family.
 */
export function bagGroupAuthorRole(
  role: UserRole | null | undefined
): BagGroupAuthorRole {
  if (role === 'director') return 'director';
  if (isFohOrManager(role)) return 'staff';
  return 'family';
}

/**
 * WHO RUNS A BAG GROUP'S LIST AND CHART. Bobbie, 2026-08-26 (ruling 6), asked
 * how far a lead's power goes, chose "RUN THEIR GROUP FULLY": a lead "should
 * be able to delete or edit same thing for managers and directors". So a lead
 * adds, edits, deletes, assigns and marks received, and taps the chart, the
 * same as Directors, Admin and Managers, in HER OWN group.
 *
 * `isLead` is the caller's own flag on the LIVE group: an active group, her
 * entry in memberSummaries carrying isLead, her uid in memberUids. The server
 * lane (netlify/functions/lib/bagGroupItemLane.mjs) computes it from the
 * stored group and never from a request; this predicate is its mirror for the
 * screen.
 *
 * A lead whose role canJoinBagGroup leaves out (an Executive Teacher or Jr
 * Staff who is a comp parent) keeps today's read-only page until Bobbie
 * answers plan question Q5. Renaming the group, its season, archiving it and
 * naming leads stay with Directors, Admin and Managers until she answers Q1.
 * Adding and removing her own group's families is NOT a Q1 edge: the plan
 * puts it in by default (section 3.4), and it runs through its own server
 * lane, netlify/functions/bagGroupFamilies.mjs, which asks this same
 * predicate of the live group before it lists or writes anything.
 */
export function canRunBagGroupList(
  role: UserRole | null | undefined,
  isLead: boolean
): boolean {
  return isDirectorFohOrManager(role) || (isLead === true && canJoinBagGroup(role));
}

export function canViewAllAttendance(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager';
}

/**
 * WHO READS THE ATTENDANCE REPORT AND THE CONCERNS LIST (Bobbie, 2026-09-01,
 * in those words: "the attdance report concerns report needs to be shown to
 * all teachers, managers, and directors", then Jr Staff and Admin confirmed
 * in as well). Every staff role. Spelled out rather than delegating to
 * isStaffRole() so this module keeps importing nothing but a TYPE from
 * users.ts - hub/tests/*.test.mjs import this file directly, and a value
 * import would drag Firebase into a plain node test run.
 *
 * This SUPERSEDES the 2026-07-29 line the report page used to carry ("Jr
 * assistants and Admin cannot"). Both roles are in now.
 *
 * No Firestore rules change rides along: attendance docs already read to
 * isActiveStaffByDoc(), so every role admitted here could always fetch the
 * underlying records. What was missing was the door, which docs/lessons.md
 * names as the worse failure - the person it blocks cannot report it,
 * because there is no error to search for.
 *
 * Kept SEPARATE from canViewAllAttendance above on purpose. That predicate
 * decides how wide the class PICKER on /attendance is (Director and Manager
 * browse the whole catalog; a teacher sees the classes they teach), and
 * widening it here would have silently changed that screen too.
 */
export function canViewAttendanceReports(role: UserRole | null | undefined): boolean {
  return (
    role === 'director'
    || role === 'manager'
    || role === 'company'
    || role === 'ensemble'
    || role === 'foh'
  );
}

/**
 * WHO OPENS THE COMP REPORTS (/comp-reports): the comp alternate history and
 * comp-only attendance. Bobbie, 2026-11-03: "KIM AND ALL OF THE [teachers]".
 * Director and Manager see every comp dance; any other active staff member
 * (teachers, Jr Staff, front desk) opens the page and sees only the comp
 * dances she teaches. The alternate history is scoped on the server
 * (lib/alternateHistory.mjs alternateReportScope); the attendance tab scopes
 * the same way on the page. Families and dancers never.
 */
export function canOpenCompReports(role: UserRole | null | undefined, status: unknown): boolean {
  if (status !== 'active') return false;
  return role === 'director' || role === 'manager' || role === 'company' || role === 'ensemble' || role === 'foh';
}

/** Director and Manager see every comp dance on the comp reports. */
export function seesEveryCompDanceOnReports(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager';
}

/**
 * WHO MAY EMAIL FAMILIES FROM THE CONCERNS LIST. The concerns page carries a
 * bulk "email these rec families to check in" composer, which is a different
 * power from reading the list - it sends studio mail out under the studio's
 * name. Bobbie's 2026-09-01 ruling opened the REPORT to every staff role and
 * said nothing about the composer, so the composer stays where outbound
 * studio mail already lives: canSendStudioAnnouncement's two roles. A Jr
 * Staff assistant reading the flag list does not also get a button that
 * mails twelve families.
 */
export function canEmailAttendanceConcerns(role: UserRole | null | undefined): boolean {
  return canSendStudioAnnouncement(role);
}

/**
 * WHO CAN OPEN /attendance ITSELF - the marking screen. Teaching staff only:
 * Attendance.tsx has gated out Admin (foh) since the 2026-08-15 audit
 * ("front desk never had a surface for it, Attendance is teaching-staff
 * only" - see the isTeachingStaff comment there), and the 2026-09-01
 * report widening did NOT reopen that question. This predicate exists so
 * the screens that LINK into /attendance (a concern row, the report's Take
 * button) can ask the same question the page's own gate asks instead of
 * hard-coding a copy that drifts: PR #820's Codex review caught exactly
 * that drift - the newly admitted Admin reader tapping a concern row was
 * sent straight into /attendance's lock screen.
 */
export function canOpenAttendancePage(role: UserRole | null | undefined): boolean {
  return (
    role === 'director'
    || role === 'manager'
    || role === 'company'
    || role === 'ensemble'
  );
}

/**
 * WHO MARKS A DANCER ABSENT AHEAD, WITH A NOTE FOR THE TEACHER (Bobbie,
 * 2026-10-24: front desk "SHOULD not be able to take" attendance, "but they
 * should be able to mark that someone will be absent and make a note" the
 * teacher sees while taking attendance). Mirrors STAFF_ABSENCE_ROLES in
 * netlify/functions/lib/staffAbsenceNotice.mjs, where it is enforced.
 */
export function canMarkAbsenceAhead(role: UserRole | null | undefined): boolean {
  return role === 'foh' || role === 'manager' || role === 'director';
}

/**
 * WHO READS JR STAFF ATTENDANCE RECORDS - the staffAttendance collection.
 * Mirrors the deployed read rule (shared/firestore.rules, staffAttendance
 * block): director, manager, company, foh. Ensemble is excluded ON PURPOSE
 * and must stay excluded - the rule's own comment: this is
 * employment-adjacent data about minors, and admitting the Jr Staff role
 * would let any teenager read every other teenager's absence history.
 *
 * Exists so screens that MOUNT a staffAttendance listener (the Jr
 * assistants section of /attendance/report) can ask the rule's question
 * before subscribing. The 2026-09-01 widening let ensemble open that
 * report, and the section then greeted them with a raw permission error -
 * the Fable review round on PR #820 caught it. Gate the section, never
 * widen the rule.
 */
export function canReadJrStaffAttendanceRecords(role: UserRole | null | undefined): boolean {
  return (
    role === 'director'
    || role === 'manager'
    || role === 'company'
    || role === 'foh'
  );
}

/**
 * EVERY STAFF MEMBER READS THE INJURY LOG, JR STAFF INCLUDED (Bobbie,
 * 2026-08-21, in DECISIONS.md, verbatim: "All staff can read the injury log.
 * They need to. Other parents cannot. Jr staff needs to."). Her reasoning is
 * operational: the person on the floor is the one who has to know a dancer
 * cannot land a tumbling pass tonight, and that person is often the Jr
 * assistant. Ensemble joined 2026-08-29 (PR #782) - this predicate had
 * shipped without it, quoting a rules comment the record had superseded.
 * Families are NOT here; the own-dancer family read lane is queued (injury
 * round 2). Mirrors the injuryNotes read rule.
 */
export function canReadInjuries(role: UserRole | null | undefined): boolean {
  return (
    role === 'director'
    || role === 'manager'
    || role === 'company'
    || role === 'foh'
    || role === 'ensemble'
  );
}

/**
 * MANAGERS WRITE INJURY RECORDS (Bobbie, 2026-08-13, in those words). The
 * Manager spec's "injury board is read-only" line is superseded for managers.
 * Mirrors canManageInjuryNotes() in shared/firestore.rules. DELETING an
 * injury record stays Director-only in the rule, for every role.
 *
 * NARROWED 2026-08-29: marking a dancer cleared is no longer part of this
 * lane. "Only directors can clear them" (Bobbie, in those words) - see
 * canClearInjuries below. Managing still covers the note text and the
 * doctor-note flag.
 */
export function canManageInjuries(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'company' || role === 'foh' || role === 'manager';
}

/**
 * ALL STAFF LOG INJURIES (Bobbie, 2026-08-29: "All staff should log
 * injuries", agreeing with her 2026-08-21 "All staff can write"). Every
 * staff role, Jr Staff (ensemble) included, can CREATE an injury entry.
 * Editing an EXISTING record stays with canManageInjuries - "add notes"
 * is a create, not a rewrite of someone else's entry. Mirrors
 * canLogInjuryNotes() in shared/firestore.rules.
 */
export function canLogInjuries(role: UserRole | null | undefined): boolean {
  return canManageInjuries(role) || role === 'ensemble';
}

/**
 * ONLY DIRECTORS CLEAR INJURIES (Bobbie, 2026-08-29, in those words: "Only
 * directors can clear them"). Clearing is any change to clearedDate - marking
 * cleared, logging an injury as already-cleared, or undoing a clear. The
 * rules enforce the same line on create and update, so this gate is honesty
 * on the screen, not the permission itself.
 */
export function canClearInjuries(role: UserRole | null | undefined): boolean {
  return role === 'director';
}

export function canSendStudioAnnouncement(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager';
}

/**
 * EMAIL ADDRESSES ARE MANAGER AND DIRECTOR ONLY (Bobbie, 2026-08-21): "we
 * don't want emails visible to anyone but managers and directors. Everyone
 * else sees a name and can message in the app only no emails needed."
 *
 * Everyone else - teachers, Jr staff, front of house, parents, dancers - sees
 * a NAME and messages in the app. Nobody needs an address to reach anyone
 * here, so showing one is a leak with no upside.
 *
 * TWO THINGS THIS DOES NOT COVER, on purpose:
 * - Your OWN address. Showing you your own sign-in is not exposure, and
 *   Account, Landing and Register all legitimately do it.
 * - SEARCHING by address. Typing an email to find someone reveals nothing to
 *   the person typing that they did not already know, so teacherSearchText
 *   still matches on it. Matching is not displaying.
 *
 * This is a SCREEN gate over data staff can already read. The rules-side
 * question - whether a teacher's account query should return the field at all
 * - is the stronger fix and is filed in the queue; a client `if` is not a
 * permission (CLAUDE.md).
 */
export function canSeeEmailAddresses(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager';
}

/**
 * A COLLEAGUE'S EMAIL IS FOR EVERY STAFF MEMBER (Bobbie, 2026-10-07,
 * DECISIONS 123 item 10, "Emails to all staff"). Every ACTIVE staff role,
 * Jr Staff included, sees a colleague's address on the Staff page. Phone
 * numbers stay private: nothing behind this answer ever shows one.
 *
 * WHY A SECOND GATE rather than widening canSeeEmailAddresses above. That one
 * also decides FAMILY addresses (the Dancers page's parent-email search) and
 * the message picker's sublabel, where the recipient can be a family account.
 * Her ruling is about colleagues and nothing else, so the family answer stays
 * Director and Manager only, exactly as it was.
 *
 * NOT THE PERMISSION, and not the data either. The address on the Staff page
 * comes from the server lane netlify/functions/staffEmailDirectory.mjs, which
 * checks the caller's role from users/{uid} itself and returns a name and an
 * address per staff account, never a phone and never a family. This answer
 * only decides whether the page asks. It mirrors that lane's own check
 * (lib/staffEmailDirectory.mjs, isLiveStaffAccount): the five staff roles and
 * status active. A Jr Staff is 'ensemble' on either sign-in, so both of hers
 * answer true here and see the same list.
 */
const COLLEAGUE_EMAIL_ROLES: readonly UserRole[] = ['director', 'manager', 'company', 'ensemble', 'foh'];

export function canSeeColleagueEmails(
  role: UserRole | null | undefined,
  status: string | null | undefined
): boolean {
  return status === 'active' && !!role && COLLEAGUE_EMAIL_ROLES.includes(role);
}

/**
 * COUNTDOWNS ARE DIRECTOR AND MANAGER WORK (Bobbie, 2026-08-14 night: "Only
 * director and managers can add countdowns"), and FOH Admin is OUT on both
 * layers as of 2026-08-15 ("Director + Manage yes! on countdown"). The rules
 * lane used to be wider - countdownEvents ran on
 * isDirectorBootstrapOrFohOrManager and admitted FOH Admin, which was a live
 * door predating packet 24, not a theoretical delta. That door is closed:
 * countdownEvents now spells its own ['director', 'manager'] list, while the
 * shared helper stays untouched because it also gates openHours, which FOH
 * Admin keeps. So this predicate and the rule agree exactly, and every
 * countdown surface - the Dates card on Calendar, the day-tap door, the card
 * on Studio settings - renders behind this one function.
 */
export function canManageCountdowns(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager';
}

/**
 * WHO READS AND EDITS TEACHER COMP INFO (DECISIONS 164). Bobbie, verbatim:
 * "this area should not be visiable to jr staff, dancers, or parents. only
 * exeutive teacers, managers, and directors", and on 2026-10-19 she kept front
 * desk in (DECISIONS 133 item 10). Client mirror of the eventStaffInfo read in
 * shared/firestore.rules (isDirectorBootstrapOrAdultStaff) and of
 * isTeacherCompInfoUserDoc in netlify/functions/lib/eventStaffInfoWrite.mjs,
 * which are where the boundary is ENFORCED. 'ensemble' is out on purpose: a Jr
 * Staff gets no tab on either sign-in. tests/teacher-comp-info.test.mjs fails
 * if this, the lane and the rule ever name different roles.
 */
export function canSeeTeacherCompInfo(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager' || role === 'company' || role === 'foh';
}

/**
 * WHO MARKS JR STAFF ATTENDANCE (Bobbie, 2026-08-16, verbatim): "3 teachers
 * managers and directors". Client mirror of canMarkJr() in the
 * staffAttendance block of shared/firestore.rules, which carries the same
 * three roles and is where the boundary is actually ENFORCED.
 *
 * Two roles are deliberately out, for different reasons. 'ensemble' IS the Jr
 * Staff role, so admitting it would mean two teenagers confirming each other,
 * which is the whole point of the record. 'foh' is out on the ruling above:
 * front desk never had a surface for it (Attendance is teaching-staff only)
 * while the rules quietly granted the write, and the 2026-08-15 audit found
 * the screen, the rules and the Jr Staff guide each saying something
 * different. Reading these records is a separate question and did NOT move:
 * front desk still reads them, it just never marks.
 */
export function canMarkJrStaffAttendance(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager' || role === 'company';
}

/**
 * WHO SEES THE FINAL PRODUCTION REPORT (Bobbie, 2026-09-01, verbatim): "we
 * need all teachers managers and admin not jr staff. need to be able to see
 * the final comp and recital song list, costums, and mussic. in one report
 * list. under comp and recital tracker". Director is folded in for the same
 * reason she is folded into every other staff-facing gate on this page - she
 * outranks every role named here. Ensemble (Jr Staff) is OUT, in those exact
 * words, and family is not a studio role this report was ever asked about.
 * Mirrors nothing in shared/firestore.rules on purpose: the report reads
 * only three things, verified line by line rather than assumed. The
 * caller's own user doc (users/{uid}, selfUid(uid) at rules:~1437) - it
 * never reads any OTHER user's doc, and it never reads students at all.
 * Offerings, open to any active user (isActiveUserByDoc() at rules:~2126),
 * wider than this predicate on purpose. Costumes, whose list read resolves
 * through isStaffOrProbingMissingDoc() down to isDirectorBootstrapOrActiveStaff()
 * (rules:~950), which covers director/manager/company/ensemble/foh - wider
 * than this predicate too, so every role admitted here already clears it.
 * The client gate above is the only boundary this feature needs.
 */
export function canViewProductionReport(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager' || role === 'company' || role === 'foh';
}

/**
 * WHO SEES EVERY PERFORMING CLASS on the Recital/Comp Tracker board itself,
 * not only the ones they teach (Bobbie, 2026-08-28, verbatim): "The comp
 * recital tracker Needs to be available for all teachers (not jr staff).
 * Teachers need to be able to see all of the songs and costumes so no one
 * picks the [same] song." Whoever can already track the whole board
 * (isDirectorFohOrManager) plus a teacher (company), so two teachers can
 * see the same list and compare notes before either commits to a song.
 * Jr Staff (ensemble) are excluded in those exact words and keep the
 * narrower, teach-only list ProductionTracker.tsx's visibleOfferings falls
 * back to for every role this predicate does not admit.
 *
 * SIGHT ONLY. This governs visibleOfferings, never canTrack - a teacher
 * gets to look at every other class's row, not edit it. canTrack stays
 * isDirectorFohOrManager exactly as it was; keep these two predicates
 * apart on purpose.
 */
export function canSeeWholeProductionBoard(role: UserRole | null | undefined): boolean {
  return isDirectorFohOrManager(role) || role === 'company';
}

/**
 * WHO SEES SUBMISSION PROGRESS: how much of the production work is done and
 * who still owes it. Bobbie's correction, 2026-09-01, verbatim, and this is
 * the whole spec:
 *
 *   "Everybody can see the song names that's not something that needs to
 *   change or gated in anyway what needs to make sure it's changing is that
 *   I don't want a parent or junior staff to see Shay has only submitted
 *   three of five songs. I want them that only be for the teachers managers
 *   and directors."
 *
 * So the gated thing is NOT the songs. Song names stay open to everybody,
 * deliberately and by her ruling: the open `allow read: if isActiveUserByDoc()`
 * on offerings is settled behaviour, not a gap to close.
 *
 * The gated thing is the DERIVED PROGRESS VIEW: per-teacher tallies, counts
 * of what is missing, outstanding lists, chase lists, "N still needed"
 * badges, and anything that adds up to the same sentence.
 *
 * 'foh' is in this list. She said "teachers managers and directors" here,
 * and on 2026-09-01 about the same page she said "all teachers managers and
 * admin not jr staff" - Admin IS foh, and she has never once grouped Admin
 * with parents or Jr Staff. Called out in PR #838 so she can correct it if
 * that reading is wrong.
 *
 * HONEST ABOUT ITS OWN REACH. This is a CLIENT boundary and it costs zero
 * rules bytes, which is the only kind available: the whole progress view is
 * derived from data the excluded roles can already read. Music and song-name
 * progress falls out of offering.musicLinks and offering.songName on the
 * offering doc itself, readable by every active account including parents.
 * Costume and eval progress needs the costumes and offeringStaffNotes
 * collections, which are staff-wide reads, so Jr Staff can reach those
 * inputs too. Nobody should read this predicate as a wall. It is the honest
 * maximum at zero rules cost, and PR #838 says so in as many words.
 */
export function canSeeProductionProgress(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager' || role === 'foh' || role === 'company';
}

/**
 * WHO EDITS A CLASS (bug B9). Bobbie, 2026-09-01: "Managers need to be able
 * to have the classes section in the hambuger menu and be able to edit class
 * times, teachers, etc." (DECISIONS.md, 2026-09-01). Director and Manager, on
 * any class: the catalog Edit form and the class page's Name & song, Notes
 * (the family facing note) and Recital details.
 *
 * The boundary is ENFORCED in the server lane (managerOfferingUpdateValid in
 * netlify/functions/lib/offeringWrite.mjs), which holds six fields back for
 * the Director. Never draw a control over one of those with this predicate:
 * alternates, the comp look, the Helper link, or a music or video removal.
 * Those stay on the class page's own Director flag.
 */
export function canEditClassDetails(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager';
}

/**
 * WHO ATTACHES A JR STAFF TO A CLASS (bug B10). Bobbie, 2026-08-28, asked
 * directly: "Admin managers and directors." Teachers may not; she chose that
 * over the teacher option (DECISIONS.md, "Who attaches a Jr Staff to a
 * class"). ENFORCED in the server lane (setOfferingAssistant in
 * netlify/functions/lib/offeringWrite.mjs), which also refuses anyone front
 * desk or a Manager adds who is not an active Jr Staff. Front desk changes
 * the Jr assistant list and nothing else on a class.
 */
export function canAttachJrAssistant(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager' || role === 'foh';
}

/**
 * WHO ADDS, CHANGES AND REMOVES A CLASS'S WEEKLY TIMES. Bobbie, 2026-09-01
 * (late): "Yes managers can update weekly Class time." (DECISIONS.md: add,
 * edit and remove). A Director writes classSeries directly, as before; a
 * Manager's writes go through lib/classSeriesWrite.mjs, because the client
 * rules for classSeries stay Director-only. Removing a time cancels only the
 * upcoming sessions of that one time, never any other booking.
 */
export function canManageClassSchedule(role: UserRole | null | undefined): boolean {
  return role === 'director' || role === 'manager';
}

/**
 * WHO CREATES OR DELETES A WHOLE CLASS. DIRECTOR ONLY. The 2026-09-01 ruling
 * opened editing an existing class and held creating and deleting one back,
 * and nothing since has changed that (DECISIONS.md, 2026-09-01). Mirrors the
 * offerings create lane and the offerings delete rule, both Director only.
 */
export function canCreateOrDeleteClass(role: UserRole | null | undefined): boolean {
  return role === 'director';
}
